import { apiFetch } from './client';

export interface LoginResponse {
  token: string;
  fullName: string;
  isoCode: string;
  phone: string;
}

export interface RegisterPayload {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  countryCode: string; // dial code e.g. "+254"
  isoCode: string;     // ISO 3166-1 alpha-2 e.g. "KE"
}

export async function loginWithEmail(
  email: string,
  password: string
): Promise<LoginResponse> {
  const res = await apiFetch('POST', '/login', { email, password });

  if (!res.ok) {
    const message =
      (res.data as any)?.message ||
      (res.data as any)?.error ||
      `Login failed (${res.status})`;
    throw new Error(message);
  }

  const authHeader = res.headers.get('Authorization') || res.headers.get('authorization') || '';
  const token = authHeader.replace(/^Bearer\s+/i, '').trim();

  if (!token) {
    throw new Error('No token received from server.');
  }

  const body = res.data as any;
  return {
    token,
    fullName: body?.Name  ?? '',
    isoCode:  body?.isoCode ?? '',
    phone:    body?.phone   ?? '',
  };
}

export async function registerUser(payload: RegisterPayload): Promise<void> {
  const fullPhone = `${payload.countryCode}${payload.phoneNumber.replace(/^0+/, '')}`;

  const res = await apiFetch('POST', '/register/user', {
    fullName: `${payload.firstName} ${payload.lastName}`.trim(),
    email: payload.email,
    phone: fullPhone,
    password: payload.password,
    isoCode: payload.isoCode,
  });

  if (!res.ok) {
    const message =
      (res.data as any)?.message ||
      (res.data as any)?.error ||
      `Registration failed (${res.status})`;
    throw new Error(message);
  }
}

export async function validateGoogleToken(
  idToken: string
): Promise<{ token: string }> {
  let email = '';
  try {
    const payload = JSON.parse(atob(idToken.split('.')[1]));
    email = payload.email || '';
  } catch { /* ignore decode errors */ }

  const res = await apiFetch('POST', '/validate-token', { idToken, email });

  if (!res.ok) {
    const message =
      (res.data as any)?.message ||
      (res.data as any)?.error ||
      `Google sign-in failed (${res.status})`;
    throw new Error(message);
  }

  const authHeader = res.headers.get('Authorization') || res.headers.get('authorization') || '';
  const token = authHeader.replace(/^Bearer\s+/i, '').trim();

  if (!token) {
    throw new Error('No token received from server.');
  }

  return { token };
}

export async function forgotPassword(email: string): Promise<void> {
  const res = await apiFetch('POST', '/forgot-password', { email });
  if (!res.ok) {
    const message = (res.data as any)?.message || `Request failed (${res.status})`;
    throw new Error(message);
  }
}

export async function resetPassword(token: string, newPassword: string): Promise<void> {
  const res = await apiFetch('POST', '/reset-password', { token, newPassword });
  if (!res.ok) {
    const message = (res.data as any)?.message || `Reset failed (${res.status})`;
    throw new Error(message);
  }
}
